import type { LocationQueryRaw } from "vue-router";
import { type RouteLocationRaw } from "vue-router";
import urlSlug from "url-slug";
import { createLocalizedRoute } from "@/router/helpers";
import { ItemStatusToUrlString } from "@/services/collection";
import { ItemStatus } from "@/services/collection/item";
import { type UrlFlags as ChatUrlFlags } from "@/composables/chat";
import { RouteName } from "@/router/routeNameEnum";
import { type Uuid } from "@/types";
import { type Category } from "@/services/collection/categories";
import type { CollectorsFilter } from "@/services/collectors";
import type { CollectionItemId } from "@/services/collection/item";
import { type CollectionItem } from "@/services/collection/item";
import type { PublicCollectionItem_FromCollectible } from "@/services/collection/item/public";
import { type PublicCollectionItem_ListItem, type PublicCollectionItem, PublicCollectionItemsFilter } from "@/services/collection/item/public";
import type { CollectibleId } from "@/services/collectibles";
import { type UserId } from "@/services/user";
import { type MarketplaceItemId } from "@/services/marketplace/item";
import { type OrderId } from "@/services/marketplace/order";
import type { CollectionItemElastic } from "@/services/collection/item/elastic";

/**
 * ==============================================
 * Messages
 * ==============================================
 */

export const createMessagesLink = (buddyId?: UserId, buddyNickname?: string, flags?: ChatUrlFlags): RouteLocationRaw => {
  const route: RouteLocationRaw = {
    name: RouteName.Messages,
  };

  if (buddyId !== undefined && buddyNickname !== undefined) {
    route.params = { buddyId, buddyNickname };
  }

  if (flags !== undefined) {
    route.query = flags;
  }

  return createLocalizedRoute(route);
};

/**
 * ==============================================
 * Collectibles (database/catalogue)
 * ==============================================
 */

export const createCatalogueLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Catalogue });

/** Catalogue item link from a category's url slug directly — for callers that have the slug but not the full Category. */
export const createCollectibleLinkByCategoryUrl = (categoryUrl: string, itemId: CollectibleId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueItem,
    params: { categoryUrl, itemId },
  });

export const createCollectibleLink = (category: Category, itemId: CollectibleId): RouteLocationRaw => createCollectibleLinkByCategoryUrl(category.url, itemId);

export const createCatalogueCategoryLink = (category: Category): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CatalogueCategory,
    params: {
      categoryUrl: category.url,
    },
  });

/**
 * ==============================================
 * Auth
 * ==============================================
 */

export const createRegistrationLink = (email?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Registration,
    query: { email },
  });

export const createLoginLink = (redirectAfterLogin?: string, email?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Login,
    query: {
      redirect_url: redirectAfterLogin,
      email,
    },
  });

export const createPasswordRestoreLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.PasswordRestore });

/**
 * ==============================================
 * Magazine
 * ==============================================
 */

export const createArticleLink = (id: number, url: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Article,
    params: { id, url },
  });

export const createNewsArticleLink = (slug: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.NewsArticle,
    params: { slug },
  });

export const createMagazineLink = (category?: Category): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Magazine,
    params: {
      category: category ? category.url : undefined,
    },
  });

/**
 * ==============================================
 * Collection
 * ==============================================
 */

export const createMyItemDetailLinkById = (id: CollectionItemId, name: string, hash?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MyItemDetail,
    params: {
      id,
      nameSeoSlug: urlSlug(name),
    },
    hash,
  });

export const createMyItemDetailLink = (item: PublicCollectionItem_ListItem | CollectionItem | PublicCollectionItem | PublicCollectionItem_FromCollectible | CollectionItemElastic, hash?: string): RouteLocationRaw => createMyItemDetailLinkById(item.id, item.name, hash);

export const createCollectionItemNewLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.CollectionItemNew });

/**
 * ==============================================
 * Curated lists
 * ==============================================
 */

export const createCuratedListsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.CuratedLists });

export const createCuratedListLink = (id: number, slug?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CuratedListDetail,
    params: { id, slug: slug || undefined },
  });

export const createCuratedListPublicProgressLink = (id: number, nickname: string, slug?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CuratedListPublicProgress,
    params: { id, nickname, slug: slug || undefined },
  });

export const createCuratedListUserProgressLink = (nickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.CuratedListUserProgress,
    params: { nickname },
  });

/**
 * ==============================================
 * AI identification
 * ==============================================
 */

export const createAiHistoryLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.AiHistory });

export const createAiLookupFormLink = (categoryUrl?: string): RouteLocationRaw => {
  if (categoryUrl !== undefined) {
    return createLocalizedRoute({ name: RouteName.AiFormWithPreselectedCategory, params: { seoSlug: categoryUrl } });
  } else {
    return createLocalizedRoute({ name: RouteName.AiForm });
  }
};

export const createAiDetailLink = (itemId: Uuid): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.AiDetail,
    params: { itemId },
  });

/**
 * ==============================================
 * Account
 * ==============================================
 */

export const createAccountSettingsLink = (anchor?: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.AccountSettings,
    hash: anchor ? `#${anchor}` : undefined,
  });

export const createYearInReviewLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.YearInReview });

export const createLeaderboardLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Leaderboard });

/**
 * ==============================================
 * Marketplace
 * ==============================================
 */

export const createShoppingCartLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.ShoppingCart });

export const createMarketplaceOrderLink = (sellerNickname: string): RouteLocationRaw => createLocalizedRoute({ name: RouteName.MarketplaceOrder, params: { sellerNickname } });

export const createSalesLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Sales });

export const createSaleLink = (orderId: OrderId): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Sale, params: { orderId } });

export const createPurchasesLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Purchases });

export const createPurchaseLink = (orderId: OrderId): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Purchase, params: { orderId } });

export const createNegotiationsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Negotiations });

export const createMarketplaceLink = (query?: LocationQueryRaw): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Marketplace, query });

export const createMarketplaceItemLink = (marketplaceItemId: MarketplaceItemId, itemName: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItem,
    params: { marketplaceItemId, seoSlug: urlSlug(itemName) },
  });

export const createMarketplaceItemSavedLink = (collectionItemId: CollectionItemId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItemSaved,
    params: { collectionItemId },
  });

export const createMarketplaceItemNewLink = (collectionItemId: CollectionItemId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItemNew,
    params: { collectionItemId },
  });

export const createMarketplaceItemEditLink = (collectionItemId: CollectionItemId): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.MarketplaceItemEdit,
    params: { collectionItemId },
  });

/**
 * ==============================================
 * Legals
 * ==============================================
 */

export const createGdprLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Gdpr });

export const createTacLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Tac });

export const createGuideLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Guide });

/**
 * ==============================================
 * Others / not sorted yet
 * ==============================================
 */

export const createMyProfileLink = (filter?: PublicCollectionItemsFilter.Filter): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collector,
    query: filter ? PublicCollectionItemsFilter.createUrlParams(filter) : undefined,
  });

export const createCollectionLink = (): RouteLocationRaw => createCollectionStatusLink(ItemStatus.InMyCollection);

export const createCollectionStatusLink = (itemStatus?: ItemStatus): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collection,
    params: { itemStatus: itemStatus ? ItemStatusToUrlString[itemStatus] : undefined },
  });

export const createError404Link = (): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Error404,
    params: {
      pathMatch: "404",
    },
  });

export const createCollectorLink = (nickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collector,
    params: { nickname },
  });

export const createHomepageLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Homepage });

export const createPublicItemDetailLink = (itemId: number, itemName: string, collectorNickname: string): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.PublicItemDetail,
    params: {
      nickname: urlSlug(collectorNickname),
      itemId: itemId,
      slug: urlSlug(itemName) || "-",
    },
  });

export const createAboutLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.About });

export const createContactsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Contacts });

export const createWhatColectioOffersLink = (anchor?: WhatColectioOffersAnchor): RouteLocationRaw => createLocalizedRoute({ name: RouteName.WhatColectioOffers, hash: anchor ? `#${anchor}` : undefined });

export enum WhatColectioOffersAnchor {
  SecureMarketplace = "secure-marketplace",
  AiFeatures = "ai-features",
  UniqueCollection = "unique-collection",
}

export const createCollectorsLink = (filter?: CollectorsFilter.Filter): RouteLocationRaw =>
  createLocalizedRoute({
    name: RouteName.Collectors,
    query: filter?.createUrlParams(),
  });

export const createSavedItemsLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.SavedItems });

export const createSupportLink = (): RouteLocationRaw => createLocalizedRoute({ name: RouteName.Support });
